import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSajuHistory, type SajuHistoryItem } from '@/hooks/useSajuHistory';
import { HEAVENLY_STEMS, EARTHLY_BRANCHES } from '@/utils/sajuCalculations';

interface SajuHistoryProps {
  onSelect: (item: SajuHistoryItem) => void;
  onClose: () => void;
}

function formatDate(timestamp: number, isEn: boolean): string {
  const date = new Date(timestamp);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  if (isEn) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()} ${hh}:${mm}`;
  }
  return `${date.getMonth() + 1}월 ${date.getDate()}일 ${hh}:${mm}`;
}

function formatBirthInfo(item: SajuHistoryItem, isEn: boolean): string {
  const timeStr = item.unknownTime
    ? (isEn ? 'Time unknown' : '시간 모름')
    : `${String(item.hour).padStart(2, '0')}:${String(item.minute).padStart(2, '0')}`;
  return `${item.year}. ${String(item.month).padStart(2, '0')}. ${String(item.day).padStart(2, '0')} ${timeStr}`;
}

export default function SajuHistory({ onSelect, onClose }: SajuHistoryProps) {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const { history, isLoaded, removeFromHistory, clearHistory, updateNote } = useSajuHistory();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editNote, setEditNote] = useState('');
  const [showConfirmClear, setShowConfirmClear] = useState(false);

  if (!isLoaded) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{isEn ? 'Loading...' : '불러오는 중...'}</p>
        </div>
      </div>
    );
  }

  const handleEditNote = (item: SajuHistoryItem) => {
    setEditingId(item.id);
    setEditNote(item.note || '');
  };

  const handleSaveNote = () => {
    if (editingId) {
      updateNote(editingId, editNote);
      setEditingId(null);
      setEditNote('');
    }
  };

  const handleClear = () => {
    clearHistory();
    setShowConfirmClear(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full max-w-md max-h-[85vh] rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {isEn ? 'Recent Readings' : '최근 본 사주'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isEn ? 'Up to 20 saved' : '최대 20개까지 저장됩니다'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-500" />
          </button>
        </div>

        {/* 리스트 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {history.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-history-line text-2xl text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">
                {isEn ? 'No saved readings yet' : '아직 저장된 사주가 없어요'}
              </p>
              <p className="text-gray-400 text-xs mt-1">
                {isEn ? 'Readings are saved automatically' : '사주를 계산하면 자동으로 저장됩니다'}
              </p>
            </div>
          ) : (
            history.map((item, index) => (
              <div
                key={item.id}
                className="bg-gray-50 rounded-2xl p-4 hover:bg-gray-100 transition-colors group"
              >
                <div className="flex items-start gap-3">
                  {/* 순번 */}
                  <div className="w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {index + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* 4주 */}
                    <div className="flex items-center gap-1 mb-2">
                      {[
                        item.saju.yearPillar,
                        item.saju.monthPillar,
                        item.saju.dayPillar,
                        item.saju.hourPillar,
                      ].map((pillar, i) => (
                        pillar && (
                          <span
                            key={i}
                            className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                              i === 2 ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'
                            }`}
                          >
                            {HEAVENLY_STEMS[pillar.stemIdx]}{EARTHLY_BRANCHES[pillar.branchIdx]}
                          </span>
                        )
                      ))}
                    </div>

                    {/* 생일 정보 */}
                    <p className="text-sm text-gray-700 font-medium">
                      {formatBirthInfo(item, isEn)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {item.locationName} · {formatDate(item.timestamp, isEn)}
                    </p>

                    {/* 메모 */}
                    {editingId === item.id ? (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="text"
                          value={editNote}
                          onChange={(e) => setEditNote(e.target.value)}
                          placeholder={isEn ? 'Add a note...' : '메모 입력...'}
                          className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                          maxLength={30}
                        />
                        <button
                          onClick={handleSaveNote}
                          className="px-3 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 cursor-pointer"
                        >
                          {isEn ? 'Save' : '저장'}
                        </button>
                      </div>
                    ) : item.note ? (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <i className="ri-sticky-note-line" />
                        {item.note}
                      </p>
                    ) : null}
                  </div>

                  {/* 액션 버튼 */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => onSelect(item)}
                      className="w-8 h-8 bg-gray-900 text-white rounded-lg flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer"
                      title={isEn ? 'View Again' : '다시 보기'}
                    >
                      <i className="ri-eye-line text-sm" />
                    </button>
                    <button
                      onClick={() => handleEditNote(item)}
                      className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
                      title={isEn ? 'Memo' : '메모'}
                    >
                      <i className="ri-edit-line text-sm text-gray-500" />
                    </button>
                    <button
                      onClick={() => removeFromHistory(item.id)}
                      className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-colors cursor-pointer"
                      title={isEn ? 'Delete' : '삭제'}
                    >
                      <i className="ri-delete-bin-line text-sm text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 푸터 */}
        {history.length > 0 && (
          <div className="p-4 border-t border-gray-100">
            {showConfirmClear ? (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowConfirmClear(false)}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  {isEn ? 'Cancel' : '취소'}
                </button>
                <button
                  onClick={handleClear}
                  className="flex-1 py-3 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer"
                >
                  {isEn ? 'Clear All' : '전체 삭제'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowConfirmClear(true)}
                className="w-full py-3 text-gray-500 text-sm hover:text-red-500 transition-colors cursor-pointer"
              >
                {isEn ? 'Clear All' : '전체 삭제'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}